'use client';

import { Coins, Info } from 'lucide-react';
import { cn, formatTimeAgo, formatTvlUsd } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { V2GridAsset } from '@/lib/types/v2';

interface GridAssetsTabProps {
  assets: V2GridAsset[];
}

const ASSET_TYPE_LABELS: Record<string, string> = {
  flaunch: 'Flaunch',
  lp: 'V3 LP',
  'lp-v4': 'V4 LP',
};

function formatYield(value: string): string {
  const num = Number(value) / 1e18;
  if (num === 0) return '0';
  if (num < 0.001) return '<0.001';
  if (num < 1) return num.toFixed(3);
  if (num < 1000) return num.toFixed(2);
  return `${(num / 1000).toFixed(1)}k`;
}

function formatTokenAmount(raw: string | undefined, decimals: number | undefined): string {
  if (!raw || raw === '0') return '0';
  const num = Number(raw) / 10 ** (decimals ?? 18);
  if (num < 0.0001) return '<0.0001';
  if (num < 1) return num.toFixed(4);
  if (num < 1000) return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return `${(num / 1000).toFixed(1)}K`;
}

function formatRange(asset: V2GridAsset): string {
  if (asset.isFullRange) return 'Full Range';
  if (asset.tickLower != null && asset.tickUpper != null) {
    return `${asset.tickLower} → ${asset.tickUpper}`;
  }
  return '—';
}

export function GridAssetsTab({ assets }: GridAssetsTabProps) {
  const activeAssets = assets.filter(a => a.active);

  if (activeAssets.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
        <Coins className="h-8 w-8 mb-2 opacity-50" />
        <p className="text-sm">No assets registered</p>
        <p className="text-xs mt-1">Assets will appear here once the grid creator registers yield sources</p>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {/* Header */}
      <div className="grid grid-cols-[1fr,auto,auto,auto] gap-2 px-2 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
        <span>Asset</span>
        <span className="text-right w-14">7D Yield</span>
        <span className="text-right w-16">TVL</span>
        <span className="text-right w-14">Range</span>
      </div>

      {/* Asset rows */}
      {activeAssets.map((asset) => (
        <AssetRow key={asset.id} asset={asset} />
      ))}
    </div>
  );
}

function AssetRow({ asset }: { asset: V2GridAsset }) {
  const yield7d = formatYield(asset.yield7d);
  const hasYield = asset.yield7d !== '0';
  const tvlValue = asset.tvlUsd || asset.tvl;
  const isPriceable = asset.priceable !== false;
  const typeLabel = asset.assetType ? ASSET_TYPE_LABELS[asset.assetType] : undefined;

  const t0Amount = formatTokenAmount(asset.tvlToken0, asset.token0Decimals);
  const t1Amount = formatTokenAmount(asset.tvlToken1, asset.token1Decimals);
  const hasTokenAmounts = asset.tvlToken0 && asset.tvlToken0 !== '0';

  return (
    <div className="grid grid-cols-[1fr,auto,auto,auto] gap-2 items-center px-2 py-2 rounded-md hover:bg-muted/30 transition-colors">
      {/* Icon + Name + Type Badge */}
      <div className="flex items-center gap-2 min-w-0">
        <div className="w-7 h-7 rounded-md bg-muted/50 border border-border/50 flex items-center justify-center shrink-0 overflow-hidden">
          {asset.iconUrl ? (
            <img src={asset.iconUrl} alt={asset.name || 'Asset'} className="w-full h-full object-cover" />
          ) : (
            <Coins className="h-3.5 w-3.5 text-muted-foreground" />
          )}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-medium truncate">
              {asset.name || asset.symbol || `Asset ${asset.id.slice(0, 8)}`}
            </p>
            {typeLabel && (
              <span className="text-[9px] px-1 py-0.5 rounded bg-muted text-muted-foreground shrink-0">
                {typeLabel}
              </span>
            )}
          </div>
          {asset.tvlUpdatedAt && (
            <p className="text-[10px] text-muted-foreground">
              Updated {formatTimeAgo(asset.tvlUpdatedAt * 1000)}
            </p>
          )}
        </div>
      </div>

      {/* 7D Yield */}
      <div className="text-right w-14">
        <span className={cn(
          'text-sm font-mono',
          hasYield ? 'text-win-green' : 'text-muted-foreground'
        )}>
          {hasYield ? `+${yield7d}` : '—'}
        </span>
      </div>

      {/* TVL */}
      <div className="text-right w-16">
        {!isPriceable ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-sm font-mono text-muted-foreground flex items-center justify-end gap-0.5">
                <Info className="h-3 w-3" />
                N/A
              </span>
            </TooltipTrigger>
            <TooltipContent>Exotic pair — TVL not available</TooltipContent>
          </Tooltip>
        ) : tvlValue && tvlValue !== '0' ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-sm font-mono text-muted-foreground">
                {formatTvlUsd(tvlValue)}
              </span>
            </TooltipTrigger>
            {hasTokenAmounts && (
              <TooltipContent>
                <div className="text-xs space-y-0.5">
                  {asset.token0Symbol && <p>{t0Amount} {asset.token0Symbol}</p>}
                  {asset.token1Symbol && <p>{t1Amount} {asset.token1Symbol}</p>}
                </div>
              </TooltipContent>
            )}
          </Tooltip>
        ) : (
          <span className="text-sm font-mono text-muted-foreground">$0</span>
        )}
      </div>

      {/* Range */}
      <div className="text-right w-14">
        <span className="text-[11px] font-mono text-muted-foreground">
          {formatRange(asset)}
        </span>
      </div>
    </div>
  );
}
